/**
 * Upgrade installs, as the garage watches them.
 *
 * An upgrade is paid for, then installed over time (`garage/upgrade-time.ts` on
 * the server). Two things happen here and nothing else:
 *
 *  - **A running install's countdown is aged locally**, and the moment one hits
 *    zero the garage is re-read. The client never decides an install is ready —
 *    it asks, exactly as the research tree does.
 *  - **A ready install is announced once**: a banner, "Upgrade ready to install",
 *    whose button opens that item's workbench, where the player installs it.
 *    Once per item and level per page load, so a re-read does not re-announce,
 *    but a player who comes back tomorrow is reminded.
 *
 * The decisions are pure functions so they are tested without a timer; the
 * `watchUpgradeInstalls` wiring is the only part that touches one.
 */
import type { GarageItem } from '@/net/api';
import { showNotice } from './notification-banner';

/** Items whose install has run out on the local clock but not yet on the server's. */
export function installsDue(items: readonly GarageItem[], elapsedSeconds: number): string[] {
  return items
    .filter((i) => i.upgrade && !i.upgrade.ready && i.upgrade.secondsRemaining - elapsedSeconds <= 0)
    .map((i) => i.id);
}

/** The key one announcement is remembered by. */
export const announceKey = (item: GarageItem): string =>
  `${item.id}:${item.upgrade?.toLevel ?? 0}`;

/** Ready installs that have not been announced yet. */
export function installsToAnnounce(
  items: readonly GarageItem[],
  announced: ReadonlySet<string>,
): GarageItem[] {
  return items.filter((i) => i.upgrade?.ready && !announced.has(announceKey(i)));
}

/** Survives tab switches and remounts; a page load starts it again. */
const announced = new Set<string>();

/** Test seam. */
export function resetAnnounced(): void {
  announced.clear();
}

/**
 * Starts watching. `items` is read on every tick, so it always sees the latest
 * overview; `snapshotAt` must change whenever a fresh overview lands — the
 * countdown is aged from it. Returns the stop function.
 */
export function watchUpgradeInstalls(options: {
  items: () => readonly GarageItem[] | undefined;
  snapshotAt: () => number;
  refresh: () => unknown;
  open: (itemId: string) => void;
}): () => void {
  let askedFor = -1;

  const tick = (): void => {
    const items = options.items() ?? [];

    for (const item of installsToAnnounce(items, announced)) {
      announced.add(announceKey(item));
      showNotice({
        title: 'Upgrade ready to install!',
        lines: [`${item.name} — level ${item.upgrade!.toLevel}`],
        action: { label: 'Install', run: () => options.open(item.id) },
      });
    }

    const snapshot = options.snapshotAt();
    const elapsed = Math.floor((Date.now() - snapshot) / 1000);
    if (askedFor !== snapshot && installsDue(items, elapsed).length > 0) {
      // Once per overview: the re-read replaces the snapshot and re-arms this.
      askedFor = snapshot;
      void options.refresh();
    }
  };

  tick();
  const timer = setInterval(tick, 1000);
  return () => clearInterval(timer);
}
